/**
 * GIFT PROGRESS BAR - Gamified incentive module
 * 
 * Shows tiered free gifts based on cart total.
 * Thresholds and values are locale-aware — passed in via props from useLocalePricing.
 * 
 * US defaults:
 * - $50+ = Free Shipping (worth $9.99)
 * - $150+ = Free Hotel Slippers (worth $28)
 * - $250+ = Free Linen Spray (worth $129)
 * 
 * Layout: Always uses 3 positions (left, center, right).
 * - 3 tiers: each tier fills one position.
 * - 2 tiers: left position shows a green "start" circle, tiers go center + right.
 * 
 * The progress line is positioned dynamically using a ref to measure the actual
 * vertical center of the circles, ensuring pixel-perfect alignment on all screen sizes.
 * 
 * The bar starts at the very left edge of the container (aligned with the "FREE" title)
 * and extends to the center of the last slot.
 * 
 * Confetti burst animation when a tier gets unlocked.
 */

import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Check, Truck, Sparkles } from "lucide-react";
import type { GiftTierPricing } from "@/hooks/useLocalePricing";

export interface GiftTier {
  id: string;
  threshold: number;
  label: string;
  /** Display value, e.g. "$28" — formatted by fp() */
  value: string;
  /** Numeric value for calculations */
  numericValue: number;
  /** Lucide icon used for the first tier (shipping) */
  icon?: typeof Truck;
  /** Product image URL used for locked state (slippers, spray) */
  image?: string;
  /** srcSet for responsive images */
  imageSrcSet?: string;
}

/**
 * Build the ALL_GIFT_TIERS array from locale-aware pricing.
 * Called once per render with the current giftTiers and formatPrice.
 */
export function buildGiftTiers(
  giftTierPricing: GiftTierPricing,
  formatPrice: (amount: number) => string
): GiftTier[] {
  return [
    { 
      id: "shipping",
      threshold: giftTierPricing.shipping.threshold, 
      label: "Free Shipping", 
      icon: Truck, 
      value: formatPrice(giftTierPricing.shipping.value),
      numericValue: giftTierPricing.shipping.value,
    },
    { 
      id: "slippers",
      threshold: giftTierPricing.slippers.threshold, 
      label: "Free Welcome Pack", 
      value: formatPrice(giftTierPricing.slippers.value),
      numericValue: giftTierPricing.slippers.value,
    },
    { 
      id: "spray",
      threshold: giftTierPricing.spray.threshold, 
      label: "Free Scalp Massager", 
      value: formatPrice(giftTierPricing.spray.value),
      numericValue: giftTierPricing.spray.value,
    },
  ];
}

/** Get the active tiers based on max reachable total and locale */
export function getActiveTiers(
  allTiers: GiftTier[],
  maxReachableTotal: number,
  isUS: boolean = true
): GiftTier[] {
  let tiers = allTiers;
  // Remove free shipping for non-US (they pay shipping regardless)
  if (!isUS) {
    tiers = tiers.filter(t => t.id !== "shipping");
  }
  // Remove linen spray if max reachable total is under the spray threshold
  const sprayTier = tiers.find(t => t.id === "spray");
  if (sprayTier && maxReachableTotal < sprayTier.threshold) {
    tiers = tiers.filter(t => t.id !== "spray");
  }
  return tiers;
}

interface GiftProgressBarProps {
  cartTotal: number;
  maxReachableTotal: number;
  isUS?: boolean;
  formatPrice?: (price: number) => string;
  giftTierPricing?: GiftTierPricing;
}

/** Confetti burst component */
function ConfettiBurst({ active }: { active: boolean }) {
  if (!active) return null;
  
  return (
    <div className="absolute inset-0 pointer-events-none z-20">
      {Array.from({ length: 12 }).map((_, i) => {
        const angle = (i / 12) * 360;
        const distance = 20 + Math.random() * 15;
        const colors = ['#22c55e', '#f5c542', '#007AFF', '#3b82f6', '#a855f7'];
        const color = colors[i % colors.length];
        const size = 3 + Math.random() * 3;
        
        return (
          <div
            key={i}
            className="absolute rounded-full animate-confetti-burst"
            style={{
              width: size,
              height: size,
              backgroundColor: color,
              left: '50%',
              top: '50%',
              '--confetti-x': `${Math.cos(angle * Math.PI / 180) * distance}px`,
              '--confetti-y': `${Math.sin(angle * Math.PI / 180) * distance}px`,
              animationDelay: `${i * 30}ms`,
            } as React.CSSProperties}
          />
        );
      })}
    </div>
  );
}

/**
 * Represents a slot in the 3-position grid.
 * A slot is either a "start" marker or a gift tier.
 */
type SlotItem = 
  | { type: "start" }
  | { type: "tier"; tier: GiftTier };

// Default US gift tier pricing (fallback when prop not provided)
const DEFAULT_GIFT_TIER_PRICING: GiftTierPricing = {
  shipping: { threshold: 50, value: 9.99 },
  slippers: { threshold: 150, value: 28 },
  spray: { threshold: 250, value: 129 },
};

export function GiftProgressBar({ cartTotal, maxReachableTotal, isUS = true, formatPrice, giftTierPricing }: GiftProgressBarProps) {
  const fp = formatPrice || ((p: number) => `$${p.toFixed(2)}`);
  const tierPricing = giftTierPricing || DEFAULT_GIFT_TIER_PRICING;
  
  // Build locale-aware gift tiers
  const allGiftTiers = useMemo(() => buildGiftTiers(tierPricing, fp), [tierPricing, fp]);
  
  // Get active tiers based on max reachable total and locale
  const activeTiers = useMemo(() => getActiveTiers(allGiftTiers, maxReachableTotal, isUS), [allGiftTiers, maxReachableTotal, isUS]);
  const tierCount = activeTiers.length;
  
  // Build the slot layout
  // 3 tiers → [tier0, tier1, tier2] — 3 columns
  // 2 tiers → [start, tier0, tier1] — 3 columns, start dot on left
  // 1 tier  → [start, tier0] — 2 columns, start dot on left, tier on right end
  const slots: SlotItem[] = useMemo(() => {
    if (tierCount >= 3) {
      return activeTiers.slice(0, 3).map(t => ({ type: "tier" as const, tier: t }));
    }
    // 1 or 2 tiers: start circle on left, tiers fill remaining positions
    const result: SlotItem[] = [{ type: "start" }];
    activeTiers.forEach(t => result.push({ type: "tier", tier: t }));
    return result;
  }, [activeTiers, tierCount]);
  
  // Dynamic column count: 3 for 2+ tiers, 2 for 1 tier (start + single tier at end)
  const SLOT_COUNT = slots.length >= 3 ? 3 : 2;
  
  // Track previously unlocked tiers for confetti animation
  // Use a Set to support multiple simultaneous confetti bursts
  const prevUnlockedRef = useRef<Set<string>>(new Set());
  const [confettiTiers, setConfettiTiers] = useState<Set<string>>(new Set());
  
  // Dynamic line positioning — measure the actual circle center
  const containerRef = useRef<HTMLDivElement>(null);
  const firstCircleRef = useRef<HTMLDivElement>(null);
  const [lineTop, setLineTop] = useState<number>(52); // fallback
  
  const measureLineTop = useCallback(() => {
    if (containerRef.current && firstCircleRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const circleRect = firstCircleRef.current.getBoundingClientRect();
      // Center of the circle relative to the container
      const centerY = (circleRect.top + circleRect.height / 2) - containerRect.top;
      setLineTop(centerY);
    }
  }, []);
  
  useEffect(() => {
    measureLineTop();
    window.addEventListener('resize', measureLineTop);
    return () => window.removeEventListener('resize', measureLineTop);
  }, [measureLineTop, slots]);
  
  // Also re-measure after a short delay for layout settling
  useEffect(() => {
    const timer = setTimeout(measureLineTop, 100);
    return () => clearTimeout(timer);
  }, [measureLineTop]);
  
  // Detect newly unlocked tiers — supports multiple simultaneous unlocks with staggered delay
  useEffect(() => {
    const currentUnlocked = new Set(activeTiers.filter(t => cartTotal >= t.threshold).map(t => t.id));
    
    // Find all newly unlocked tiers
    const newlyUnlocked: string[] = [];
    Array.from(currentUnlocked).forEach(id => {
      if (!prevUnlockedRef.current.has(id)) {
        newlyUnlocked.push(id);
      }
    });
    
    if (newlyUnlocked.length > 0) {
      const timers: ReturnType<typeof setTimeout>[] = [];
      
      newlyUnlocked.forEach((id, index) => {
        // Stagger each confetti burst by 500ms
        const startDelay = index * 500;
        
        const startTimer = setTimeout(() => {
          setConfettiTiers(prev => { const next = new Set(Array.from(prev)); next.add(id); return next; });
        }, startDelay);
        timers.push(startTimer);
        
        // Clear each confetti after 800ms from its start
        const clearTimer = setTimeout(() => {
          setConfettiTiers(prev => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
        }, startDelay + 800);
        timers.push(clearTimer);
      });
      
      prevUnlockedRef.current = currentUnlocked;
      return () => timers.forEach(t => clearTimeout(t));
    }
    
    prevUnlockedRef.current = currentUnlocked;
  }, [cartTotal, activeTiers]);
  
  const maxThreshold = activeTiers[activeTiers.length - 1].threshold;
  
  // Find the next tier to unlock
  const nextTier = activeTiers.find(t => cartTotal < t.threshold);
  const amountToNext = nextTier ? nextTier.threshold - cartTotal : 0;
  const allUnlocked = !nextTier;

  // Calculate progress percentage mapped to slot positions
  // Each slot center is at (slotIndex + 0.5) / SLOT_COUNT * 100
  // The bar starts at the first slot center and ends at the last slot center
  const firstCenter = (0.5 / SLOT_COUNT) * 100;
  
  const getProgressPercent = () => {
    if (cartTotal >= maxThreshold) return ((SLOT_COUNT - 0.5) / SLOT_COUNT) * 100;
    if (cartTotal <= 0) return firstCenter; // Start at the start dot center
    
    const hasStartSlot = slots[0].type === "start";
    
    for (let i = 0; i < activeTiers.length; i++) {
      const tierThreshold = activeTiers[i].threshold;
      const prevThreshold = i === 0 ? 0 : activeTiers[i - 1].threshold;
      
      const slotIndex = hasStartSlot ? i + 1 : i;
      const tierPosition = ((slotIndex + 0.5) / SLOT_COUNT) * 100;
      
      // Previous position: for the first tier with a start slot, it's the start slot center
      const prevPosition = i === 0 
        ? (hasStartSlot ? firstCenter : (0.5 / SLOT_COUNT) * 100)
        : (((hasStartSlot ? i : i - 1) + 0.5) / SLOT_COUNT) * 100;
      
      if (cartTotal < tierThreshold) {
        const segmentProgress = (cartTotal - prevThreshold) / (tierThreshold - prevThreshold);
        return prevPosition + segmentProgress * (tierPosition - prevPosition);
      }
    }
    return ((SLOT_COUNT - 0.5) / SLOT_COUNT) * 100;
  };

  const progressPercent = getProgressPercent();
  
  // The bar spans from 0% (left edge) to the last slot center
  const lastCenter = ((SLOT_COUNT - 0.5) / SLOT_COUNT) * 100;

  return (
    <div className="bg-card border rounded-2xl p-4 sm:p-5 mb-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-[#007AFF] animate-pulse" />
          <span className="text-xs sm:text-sm font-bold text-foreground tracking-wider uppercase">
            FREE GIFTS IN YOUR CART
          </span>
        </div>
        {!allUnlocked && nextTier && (
          <span className="text-[10px] sm:text-xs text-muted-foreground">
            Add <span className="font-bold text-[#007AFF]">{fp(amountToNext)}</span><span className="hidden sm:inline"> for {nextTier.label}</span>
          </span>
        )}
        {allUnlocked && (
          <span className="text-[10px] sm:text-xs font-bold text-green-600 flex items-center gap-1">
            <Sparkles className="w-3 h-3" /> All gifts unlocked!
          </span>
        )}
      </div>

      {/* Main layout container */}
      <div className="relative" ref={containerRef}>
        {/* 3-column grid */}
        <div className={`relative z-10 grid gap-0 ${SLOT_COUNT === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
          {slots.map((slot, idx) => {
            if (slot.type === "start") {
              // Green starting dot — centered in first column
              return (
                <div key="start" className="flex flex-col items-center text-center">
                  {/* Invisible badge spacer — same height as tier badges for alignment */}
                  <div className="h-[22px] mb-1.5" />
                  
                  {/* Circle container — centered, same size as tier circles for alignment */}
                  <div ref={firstCircleRef} className="relative w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center">
                    <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-green-500 shadow-sm shadow-green-500/30" />
                  </div>
                  
                  {/* Invisible spacers for label + threshold alignment */}
                  <span className="text-[10px] sm:text-xs leading-tight mt-1.5 invisible">&nbsp;</span>
                  <span className="text-[9px] sm:text-[10px] mt-0.5 invisible">&nbsp;</span>
                </div>
              );
            }
            
            // Regular tier slot
            const tier = slot.tier;
            const Icon = tier.icon;
            const isUnlocked = cartTotal >= tier.threshold;
            const isNext = !isUnlocked && (activeTiers.findIndex(t => t.id === tier.id) === activeTiers.findIndex(t => cartTotal < t.threshold));
            const hasImage = !!tier.image;
            const showConfetti = confettiTiers.has(tier.id);
            
            return (
              <div 
                key={tier.id}
                className="flex flex-col items-center text-center"
              >
                {/* Value badge - fixed height for alignment */}
                <div className={`h-[22px] flex items-center px-2 py-0.5 rounded-full text-[8px] sm:text-[9px] font-bold whitespace-nowrap mb-1.5 transition-all duration-500 ${
                  isUnlocked 
                    ? 'bg-green-500 text-white' 
                    : 'bg-[#f5f0e6] dark:bg-[#0066CC]/50 text-foreground/70 border border-[#e8e4dd] dark:border-[#0066CC]/40'
                }`}>
                  {isUnlocked ? 'UNLOCKED' : `worth ${tier.value}`}
                </div>

                {/* Circle - sits on the progress line */}
                <div 
                  ref={idx === 0 ? firstCircleRef : undefined}
                  className={`relative w-12 h-12 sm:w-14 sm:h-14 rounded-full flex items-center justify-center transition-all duration-500 border-[3px] overflow-hidden ${
                  isUnlocked 
                    ? 'bg-green-50 dark:bg-green-900/30 border-green-500 shadow-md shadow-green-500/20' 
                    : isNext
                    ? 'bg-white dark:bg-gray-800 border-[#0066CC] dark:border-[#f5c542] shadow-md shadow-[#0066CC]/20'
                    : 'bg-white/80 dark:bg-gray-800 border-gray-300 dark:border-gray-600'
                }`}>
                  {isUnlocked ? (
                    <Check className={`w-5 h-5 sm:w-6 sm:h-6 text-green-600 ${showConfetti ? 'animate-bounce' : ''}`} />
                  ) : hasImage ? (
                    <img 
                      src={tier.image} 
                      srcSet={tier.imageSrcSet}
                      sizes="(min-width: 640px) 56px, 48px"
                      alt={tier.label}
                      className="w-full h-full object-cover rounded-full"
                      loading="lazy"
                    />
                  ) : Icon ? (
                    <Icon className={`w-5 h-5 sm:w-6 sm:h-6 ${
                      isNext 
                        ? 'text-[#0066CC] dark:text-[#f5c542]' 
                        : 'text-gray-400'
                    }`} />
                  ) : null}
                  
                  {/* Confetti burst */}
                  <ConfettiBurst active={showConfetti} />
                  
                  {/* Glow pulse on unlock */}
                  {showConfetti && (
                    <div className="absolute inset-0 rounded-full animate-glow-pulse bg-green-400/30" />
                  )}
                </div>

                {/* Label - below the circle */}
                <span className={`text-[10px] sm:text-xs font-semibold leading-tight mt-1.5 ${
                  isUnlocked ? 'text-green-700 dark:text-green-400' : 'text-foreground/70'
                }`}>
                  {tier.label}
                </span>

                {/* Threshold - formatted with locale currency */}
                <span className={`text-[9px] sm:text-[10px] mt-0.5 ${
                  isUnlocked ? 'text-green-600/70' : 'text-muted-foreground'
                }`}>
                  {fp(tier.threshold)}+
                </span>
              </div>
            );
          })}
        </div>

        {/* 
         * Progress line — absolutely positioned.
         * Starts at the center of the first slot.
         * Ends at the center of the last slot.
         * Top is dynamically measured from the first circle's center.
         */}
        <div 
          className="absolute z-0 pointer-events-none" 
          style={{ 
            left: `${(0.5 / SLOT_COUNT) * 100}%`, 
            right: `${100 - lastCenter}%`, 
            top: `${lineTop}px`,
            height: '6px',
            transform: 'translateY(-50%)',
          }}
        >
          {/* Track background */}
          <div className="h-full bg-gray-200 dark:bg-gray-700/60 rounded-full overflow-hidden">
            {/* Filled progress */}
            <div 
              className="h-full rounded-full transition-all duration-1000 ease-out relative overflow-hidden"
              style={{ 
                width: `${Math.max(0, Math.min(100, ((progressPercent - firstCenter) / (lastCenter - firstCenter)) * 100))}%`,
                background: 'linear-gradient(90deg, #22c55e, #16a34a)',
              }}
            >
              {/* Shimmer path effect moving toward the right */}
              {!allUnlocked && (
                <div 
                  className="absolute inset-0 animate-gift-shimmer"
                  style={{
                    background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.4) 40%, rgba(255,255,255,0.6) 50%, rgba(255,255,255,0.4) 60%, transparent 100%)',
                    backgroundSize: '200% 100%',
                  }}
                />
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
